import type { WorkbookColumnarSheet } from '@erp/shared-types';
import { columnReference, type ColumnType } from '@erp/workbooks';

type WorkbookLookup = WorkbookColumnarSheet['lookups'][number];

/**
 * A lookup being edited in the Add Column popover. `base` is the worksheet
 * lookup it started from; a lookup added in the popover has none.
 */
export type LookupDraftEntry = {
  base?: WorkbookLookup;
  lookup: WorkbookLookup;
};

export function lookupDraft(lookups: readonly WorkbookLookup[]) {
  return lookups.map((lookup): LookupDraftEntry => ({ base: lookup, lookup }));
}

/**
 * Writes a lookup draft onto the latest worksheet. Lookups the draft started
 * from take the draft's edits or removal; lookups changed or added elsewhere
 * while the draft was open are kept. New lookups are appended, and each new
 * lookup result becomes an output column of the type `outputType` gives it.
 */
export function mergeLookupDraft(
  sheet: WorkbookColumnarSheet,
  seed: readonly WorkbookLookup[],
  draft: readonly LookupDraftEntry[],
  outputType: (lookup: WorkbookLookup) => ColumnType,
): WorkbookColumnarSheet {
  const unclaimed = [...seed];
  const lookups: WorkbookLookup[] = [];
  for (const current of sheet.lookups) {
    const seedIndex = unclaimed.findIndex((item) => sameLookup(item, current));
    const base = seedIndex < 0 ? undefined : unclaimed.splice(seedIndex, 1)[0];
    if (!base) {
      lookups.push(current);
      continue;
    }
    // A seeded lookup with no draft entry was removed in the draft.
    const entry = draft.find((item) => item.base === base);
    if (entry) lookups.push(entry.lookup);
  }
  lookups.push(
    ...draft.filter((entry) => !entry.base).map((entry) => entry.lookup),
  );
  const previous = new Set(sheet.lookups.map((lookup) => lookup.outputColumn));
  const names = new Set(
    sheet.columns.map((column) => column.name.toLowerCase()),
  );
  const added = lookups
    .filter(
      (lookup) =>
        !previous.has(lookup.outputColumn) &&
        !names.has(lookup.outputColumn.toLowerCase()),
    )
    .map((lookup) => ({
      name: lookup.outputColumn,
      expression: columnReference(lookup.outputColumn),
      type: outputType(lookup),
    }));
  return { ...sheet, lookups, columns: [...sheet.columns, ...added] };
}

function sameLookup(left: WorkbookLookup, right: WorkbookLookup) {
  return (
    left.sourceId === right.sourceId &&
    left.inputColumn === right.inputColumn &&
    left.matchColumn === right.matchColumn &&
    left.valueColumn === right.valueColumn &&
    left.outputColumn === right.outputColumn &&
    left.missing === right.missing
  );
}
